import { useState, useEffect, useRef } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";

// Keyless address autocomplete (Photon / OpenStreetMap). Biased to the DMV.
// Fully graceful: if the lookup is unavailable, it stays a normal text input.
export const AddressAutocomplete = ({ value, onChange, testid = "bw-address" }) => {
  const [q, setQ] = useState(value || "");
  const [sug, setSug] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    if (!q || q.trim().length < 4) { setSug([]); return; }
    let active = true;
    const t = setTimeout(async () => {
      try {
        setLoading(true);
        const r = await fetch(`https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=5&lang=en&lat=38.9&lon=-77.04`);
        const data = await r.json();
        if (!active) return;
        const items = (data.features || []).map((f) => {
          const p = f.properties || {};
          const line1 = [p.housenumber, p.street || p.name].filter(Boolean).join(" ");
          const line2 = [p.city, p.state, p.postcode].filter(Boolean).join(", ");
          return [line1, line2].filter(Boolean).join(", ");
        }).filter(Boolean);
        setSug([...new Set(items)]);
      } catch {
        /* offline / blocked — plain typing still works */
      } finally {
        if (active) setLoading(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(t); };
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = (s) => { setQ(s); onChange(s); setSug([]); setOpen(false); };

  return (
    <div className="relative" ref={boxRef}>
      <MapPin className="w-4 h-4 text-leaf absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
      {loading && <Loader2 className="w-4 h-4 text-muted-foreground animate-spin absolute right-3.5 top-1/2 -translate-y-1/2" />}
      <Input data-testid={testid} className="h-12 bg-white pl-10" placeholder="Service address" autoComplete="off"
        value={q} onChange={(e) => { setQ(e.target.value); onChange(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} />
      {open && sug.length > 0 && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-border rounded-xl shadow-lg overflow-hidden max-h-56 overflow-y-auto" data-testid="bw-address-suggestions">
          {sug.map((s, i) => (
            <button key={i} type="button" onClick={() => pick(s)} data-testid={`bw-address-sug-${i}`}
              className="w-full text-left px-4 py-2.5 text-sm text-navy hover:bg-muted flex items-start gap-2">
              <MapPin className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" /> <span>{s}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
